import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-rest-console>` says about itself.
 *
 * It sends nothing. Authentication, the base address, proxying and what counts as an error all
 * belong to the application, and a component that decided any of them would be usable by exactly
 * one of them. Pressing Send raises an event; the application sends and hands back a response.
 *
 * The case worth knowing before you use it is the bodyless method. GET and HEAD carry no body, and
 * the pane says so rather than hiding the tab (which reads as a broken console) or leaving an
 * editable box whose contents are silently dropped (which leaves somebody unable to work out why
 * their body had no effect). What was typed is kept, because choosing the wrong method first is the
 * ordinary way this happens.
 */
const docs: ComponentDocs = {
  aliases: ['api console', 'http client', 'request builder', 'rest client', 'api explorer'],

  answers: [
    'compose an API request by hand and see what comes back',
    'check what headers a response actually carried',
    'try a request before writing the code that makes it',
    'see how long an API call took and how big the answer was',
  ],

  placements: ['appPage'],

  whenToUse:
    'When somebody is exploring an API rather than consuming one — composing a request, reading the ' +
    'whole response, and changing one thing at a time.',

  insteadUse: [
    {
      name: 'QueryEditor',
      when: 'the question is a QUERY rather than a request — that surface knows about tabs of saved queries.',
    },
    {
      name: 'CodeEditor',
      when: 'the text is a stored artifact being edited rather than a request being composed.',
    },
  ],

  inputs: [
    {
      name: 'method',
      attribute: 'method',
      type: 'text',
      options: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'],
      description: 'The request method. Anything unrecognised reads as GET rather than being sent as typed.',
      default: 'GET',
      readsBack: 'The resolved method — always one of the six, never whatever was written on the attribute.',
    },
    {
      name: 'uri',
      attribute: 'uri',
      type: 'text',
      description:
        'The address, set in markup. There is a `uri` getter for reading back what is composed, but ' +
        'no setter — so the page says markup only rather than promising an assignment that would ' +
        'throw. Send is disabled while it is empty, rather than raising an event with nothing in it.',
    },
    {
      name: 'headers',
      type: 'json',
      description:
        'The request headers as `{ name, value }[]`. A LIST rather than an object, because order is ' +
        'kept and a header list is not a set — the same name can legitimately appear twice.',
      default: [],
    },
    {
      name: 'body',
      type: 'json',
      description:
        'The request body text. Kept even for a method that carries none, so switching from GET to ' +
        'POST gets it back.',
    },
    {
      name: 'response',
      type: 'json',
      description:
        'What came back: `{ status, reason?, headers?, body?, tookMs?, bytes? }`. Setting it moves to ' +
        'the response body pane — the thing somebody pressed Send for should not be behind a tab they ' +
        'have to know to press. `null` clears it.',
    },
    {
      name: 'pane',
      attribute: 'pane',
      type: 'text',
      options: ['request-headers', 'request-body', 'response-body', 'response-headers'],
      description: 'Which of the four panes is showing.',
      default: 'request-headers',
    },
    {
      name: 'sending',
      attribute: 'sending',
      type: 'boolean',
      description: 'A request is in flight: Send reads "Sending…" and raises nothing.',
      default: false,
    },
    {
      name: 'sendLabel',
      attribute: 'send-label',
      type: 'text',
      description: 'What the send button is called.',
      default: 'Send',
    },
  ],

  events: [
    {
      name: 'caos-rest-send',
      when: 'Send is pressed, or Enter in the address, or Ctrl/Cmd+Enter in the body. Never while `sending`, never with an empty address.',
      carries:
        '`{ method, uri, headers, body? }` — `body` is OMITTED for a method that carries none, rather ' +
        'than sent as an empty string. An empty body and no body are different requests.',
    },
  ],

  state: [
    {
      name: 'request',
      type: '{ method, uri, headers, body? }',
      description: 'The request as it stands — exactly what the send event would carry.',
    },
    {
      name: 'carriesBody',
      type: 'boolean',
      description: 'Whether this method has a body at all. Read it to decide what to send.',
    },
  ],

  parts: [
    { part: 'console', is: 'The outer column.' },
    { part: 'request-line', is: 'The method, address and send row.' },
    { part: 'method', is: 'The method dropdown.' },
    { part: 'uri', is: 'The address box.' },
    { part: 'send', is: 'The send button.' },
    { part: 'status', is: 'The response status, timing and size line.' },
    { part: 'tabs', is: 'The four-pane tab strip.' },
    { part: 'tab', is: 'One pane tab.' },
    { part: 'pane', is: 'The showing pane.' },
    { part: 'headers', is: 'The editable request-header grid.', when: 'The request-headers pane is showing.' },
    {
      part: 'editor-surface',
      is: 'The body editing surface.',
      when: 'A body pane is showing and the built-in plain-text engine is mounted.',
    },
  ],

  accessibility:
    'The status line is a `role="status"` live region and states the code, the reason, the timing and ' +
    'the size AS WORDS — colour repeats the band, it never carries it, so a 500 is not distinguished ' +
    'from a 200 by red alone. The four panes are a real `role="tablist"` with a roving tabindex. Every ' +
    'header box carries its own numbered accessible name, so a grid of unlabelled text inputs is not ' +
    'what a screen reader finds. A method that carries no body states that in the pane as readable ' +
    'text rather than hiding the tab or disabling a box.',

  examples: [
    {
      key: 'response',
      label: 'A request and what came back',
      summary:
        'The ordinary case: the status, the timing and the size stated in words above the panes, ' +
        'with the response body showing because that is what Send was pressed for.',
      placement: {
        component: 'caos-rest-console',
        inputs: {
          method: 'GET',
          uri: '/services/data/v1/accounts?limit=2',
          pane: 'response-body',
          headers: [{ name: 'Accept', value: 'application/json' }],
          response: {
            status: 200,
            reason: 'OK',
            tookMs: 142,
            bytes: 318,
            headers: { 'content-type': 'application/json', 'x-request-id': '7c1f…' },
            body: '{\n  "records": [\n    { "id": "001C", "name": "Northwind Traders" }\n  ]\n}',
          },
        },
      },
      stage: { needsWidth: 720 },
    },
    {
      key: 'bodyless',
      label: 'A method that carries no body',
      summary:
        'Said, not hidden and not silently dropped. Anything typed is kept, so switching to POST ' +
        'gets it back — choosing the wrong method first is the ordinary way this happens.',
      placement: {
        component: 'caos-rest-console',
        inputs: { method: 'GET', uri: '/services/data/v1/limits', pane: 'request-body' },
      },
      stage: { needsWidth: 720 },
    },
    {
      key: 'error',
      label: 'A response that failed',
      summary:
        'The code and the reason are the text of the line, not its colour — a 500 read aloud says ' +
        '"500 Internal Server Error" rather than nothing.',
      placement: {
        component: 'caos-rest-console',
        inputs: {
          method: 'POST',
          uri: '/services/data/v1/accounts',
          pane: 'response-body',
          response: {
            status: 500,
            reason: 'Internal Server Error',
            tookMs: 2310,
            bytes: 96,
            body: '{ "error": "the save plan did not compile" }',
          },
        },
      },
      stage: { needsWidth: 720 },
    },
  ],
};

export default docs;
